import { Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { isWorkspaceGranted } from '../foundation/access';
import type { AccountBinding } from '../foundation/account';
import type { PanelGeneratedHttpPort, PanelLiveScreenId, PanelScreenConnection } from '../foundation/runtime-port';
import { ParteBridgeGate, type ParteBridgeHandoff } from '../foundation/parte-bridge/gate';
import { registerScreenPrefetch } from '../foundation/screen-prefetch';
import { LoadingRegion, SystemBanner } from '../foundation/state';
import { randomUuid } from '../runtime/browser-crypto';
import { preloadOperationValidators } from '../runtime/identity-http';
import type { PanelIdentityRuntime } from '../runtime/identity-runtime';
import { PANEL_SCREENS, panelScreenAtPath, projectScreenAccess, type PanelRoutedScreenId } from '../runtime/panel-screens';
import { restaurantFacts } from '../runtime/restaurant-setting';
import { scopeInstanceKey } from '../runtime/scoped-screen-runtime';
import { sessionRoleLabel } from '../foundation/transport-copy';
import type { PanelRuntimeSession } from '../runtime/session-model';
import type { RouteRecoveryAction } from './route-recovery-boundary';
import {
  noOp, type RestaurantScreenContext, type ScreenBindingProps, type TenantScreenContext,
} from './screen-bindings/binding';
import { lazyScreen, registerFailedLoadReset, resetFailedLoads, ScreenLoadBoundary } from './screen-load-boundary';

/**
 * Each screen is its own chunk, and so is the code that binds its owner ports to the client: the entry carries no
 * screen's ports or providers. The dispatcher owns the one loading
 * boundary and the one load-failure boundary.
 */
const screenPreloads = new Map<PanelLiveScreenId, () => void>();
function screenChunk<Screen extends ComponentType<never>>(load: () => Promise<Readonly<{ default: Screen }>>, preloadAs?: PanelLiveScreenId): Screen {
  const screen = lazyScreen(load);
  registerFailedLoadReset(screen.reset);
  // A preload only warms the module cache: a failed one is forgotten, and the screen's own load reports its failure.
  if (preloadAs !== undefined) screenPreloads.set(preloadAs, () => { void load().catch(() => undefined); });
  return screen.Component;
}
type RestaurantBinding = ComponentType<ScreenBindingProps<RestaurantScreenContext>>;
type TenantBinding = ComponentType<ScreenBindingProps<TenantScreenContext>>;
const AnalyticsBinding = screenChunk(() => import('./screen-bindings/analytics'), 'analytics');
const CocinaBinding = screenChunk(() => import('./screen-bindings/cocina'), 'cocina');
const ConfigurationBinding = screenChunk(() => import('./screen-bindings/configuration'), 'configuration');
const ConstructorBinding = screenChunk(() => import('./screen-bindings/constructor'), 'constructor');
const FranchiseBinding = screenChunk(() => import('./screen-bindings/franchise'), 'franchise');
const HistoryBinding = screenChunk(() => import('./screen-bindings/history'), 'history');
const HomeBoardBinding = screenChunk(() => import('./screen-bindings/home-board'), 'home-board');
const HrBinding = screenChunk(() => import('./screen-bindings/hr'), 'hr');
const InventoryBinding = screenChunk(() => import('./screen-bindings/inventory'), 'inventory');
const MarketingBinding = screenChunk(() => import('./screen-bindings/marketing'), 'marketing');
const MenuBinding = screenChunk(() => import('./screen-bindings/menu'), 'menu');
const ServicioBinding = screenChunk(() => import('./screen-bindings/servicio'), 'servicio');
const SuperAdminBinding = screenChunk(() => import('./screen-bindings/super-admin'), 'super-admin');
const TarjetasBinding = screenChunk(() => import('./screen-bindings/tarjetas'), 'tarjetas');
const TpvBinding = screenChunk(() => import('./screen-bindings/tpv'), 'tpv');

/** El Parte is its own chunk inside Home's: Home warms it. */
const preloadPathExtras = (path: string) => {
  if (path === '/app/home') void import('../screens/parte/parte-home').catch(() => undefined);
};

const screenAtPath = (path: string): PanelLiveScreenId | null => {
  for (const tenantKind of ['restaurant', 'franchise', 'platform'] as const) {
    const screen = panelScreenAtPath(path, tenantKind);
    if (screen !== null) return screen;
  }
  return null;
};

/** The validators a screen's commands and filtered reads wait for before they leave (its answers' validators load
 * beside each request). */
const preloadScreenValidators = (screen: PanelLiveScreenId) => preloadOperationValidators(PANEL_SCREENS[screen].operations, { answers: false });

/**
 * Starts loading the chunk of the screen the address names, at boot, while Identity answers: the screen then mounts
 * without waiting for its code. It loads code only; Identity still decides whether the screen opens.
 */
export function preloadScreenAtPath(path: string): void {
  const screen = screenAtPath(path);
  if (screen === null) return;
  screenPreloads.get(screen)?.();
  preloadPathExtras(path);
  preloadScreenValidators(screen);
}

// The navigation warms a destination on idle after login (its chunk) and when it is pressed or focused (its chunk and
// its commands' validators). The specs load no screen they do not open.
if (import.meta.env.MODE !== 'test') {
  registerScreenPrefetch((path, reason) => {
    const screen = screenAtPath(path);
    if (screen === null) return;
    screenPreloads.get(screen)?.();
    preloadPathExtras(path);
    if (reason === 'intent') preloadScreenValidators(screen);
  });
}

export interface ScreenRuntimeProps {
  readonly runtime: Pick<PanelIdentityRuntime, 'screenClient' | 'account' | 'logout'>
    /**
     * docs/decisions/2026-10-28-tpv-sin-conexion.md · the TPV's probe without a session, and the digest of this tab's
     * bearer its offline data is bound to.
     */
    & Partial<Pick<PanelIdentityRuntime, 'readPublicClock' | 'localSessionDigest'>>
    /** The server's clock as this device reads it: the session's instants are judged on it. */
    & Partial<Pick<PanelIdentityRuntime, 'serverNow'>>;
  readonly screen: PanelRoutedScreenId;
  /** The router's current path. */
  readonly path: string;
  readonly session: PanelRuntimeSession;
  readonly sessionActive: boolean;
  readonly connection: PanelScreenConnection;
  readonly navigation: Readonly<{ hidden: boolean; onShow: () => void }>;
  readonly onNavigate?: (path: string) => void;
  readonly onUnsentWorkChange?: (unsent: boolean) => void;
  readonly onRouteRecoveryChange?: (action: RouteRecoveryAction | null) => void;
  /** The router's query string: a prefill-bridge link from El Parte lives there (E5). */
  readonly search?: string;
  /** Replaces the query string (the bridge drops its link once used or dismissed). */
  readonly onSearchChange?: (search: string) => void;
}

/** The one «Mi cuenta» binding of a session (row 5.4b, PS-08/PS-11/PS-12):
 * the runtime's account port and its one logout, the authenticated subject
 * and the session's one role label. Every page that draws Mi cuenta gets it. */
export function accountBinding(runtime: Pick<PanelIdentityRuntime, 'account' | 'logout'>, session: PanelRuntimeSession): AccountBinding {
  return { port: runtime.account, subjectUserId: session.canonical.user.id, roleLabel: sessionRoleLabel(session, 'account'),
    createIdempotencyKey: () => randomUuid(), onLogout: runtime.logout };
}
/** The one screen dispatcher (row 5.4a, PS-01/PS-02). The key owns only the
 * React subject lifetime: a new login replaces the screen client, and the
 * transport, bearer and admission stay with PanelIdentityRuntime. */
export function ScreenRuntime(props: ScreenRuntimeProps) {
  const client = props.runtime.screenClient(props.screen);
  // An open screen's commands find their validators at hand.
  useEffect(() => { preloadScreenValidators(props.screen); }, [props.screen]);
  return <ScreenLoadBoundary onRetry={resetFailedLoads} screen={props.screen}>
    <Suspense fallback={<LoadingRegion label="Cargando la pantalla" />}>
      <ScreenSubject key={`${props.screen}:${scopeInstanceKey(client)}`} {...props} client={client} />
    </Suspense>
  </ScreenLoadBoundary>;
}

/** How each screen binds its owner ports to the one client and renders. */
type ScreenView =
  | { readonly tenant: 'restaurant'; readonly needsTenant: boolean; readonly Binding: RestaurantBinding }
  | { readonly tenant: 'franchise' | 'platform'; readonly Binding: TenantBinding };

const restaurantView = (needsTenant: boolean, Binding: RestaurantBinding): ScreenView => ({ tenant: 'restaurant', needsTenant, Binding });

const SCREEN_VIEWS: Record<PanelRoutedScreenId, ScreenView> = {
  tpv: restaurantView(false, TpvBinding),
  cocina: restaurantView(false, CocinaBinding),
  servicio: restaurantView(false, ServicioBinding),
  history: restaurantView(true, HistoryBinding),
  menu: restaurantView(true, MenuBinding),
  inventory: restaurantView(true, InventoryBinding),
  marketing: restaurantView(true, MarketingBinding),
  tarjetas: restaurantView(true, TarjetasBinding),
  constructor: restaurantView(true, ConstructorBinding),
  analytics: restaurantView(true, AnalyticsBinding),
  hr: restaurantView(true, HrBinding),
  configuration: restaurantView(false, ConfigurationBinding),
  'home-board': restaurantView(false, HomeBoardBinding),
  franchise: { tenant: 'franchise', Binding: FranchiseBinding },
  'super-admin': { tenant: 'platform', Binding: SuperAdminBinding },
};

function ScreenSubject(props: ScreenRuntimeProps & { readonly client: PanelGeneratedHttpPort }) {
  const { client, screen, session } = props;
  const view = SCREEN_VIEWS[screen];
  if (view.tenant !== 'restaurant') {
    return session.tenant.kind === view.tenant ? <view.Binding client={client} context={props} /> : null;
  }
  const { Binding } = view;
  const { tenant } = session;
  const facts = restaurantFacts(session.canonical);
  if (tenant.kind !== 'restaurant' || facts === null) return null;
  const access = projectScreenAccess(tenant.access, screen);
  if (access === null) return null;
  if (view.needsTenant && facts.tenantId === null) {
    return <SystemBanner tone="critical" urgent>No se ha podido confirmar la franquicia propietaria de este restaurante. La pantalla permanece sin datos.</SystemBanner>;
  }
  // The session object keeps its identity: the publication read is fenced on it.
  const renderRestaurant = (bridge: ParteBridgeHandoff | null): ReactNode => <Binding client={client} context={{
    ...props, session: session as RestaurantScreenContext['session'], access, facts, tenantId: facts.tenantId ?? '', bridge,
  }} />;
  // El Parte E5: a prefill link is revalidated by its owner before the screen sees any seed.
  return <ParteBridgeGate client={client} connection={props.connection} onSearchChange={props.onSearchChange ?? noOp}
    restaurantId={facts.restaurantId} screen={screen} search={props.search ?? ''} sessionActive={props.sessionActive}>
    {renderRestaurant}
  </ParteBridgeGate>;
}
